'use client';

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useDebounce } from '@/hooks/use-debounce';
import type { CoordinatedVideoState, VideoEntry, VideoEntryState, PageVideoCoordinator, SoundState } from './types';
import { electWinner, joinsWinner, RATIO_THRESHOLDS, SETTLE_MS, shouldClaimSound, withOwner } from './utils';

const CoordinatorContext = createContext<PageVideoCoordinator | null>(null);

/**
 * Elects the most-visible eligible video to play, plus any `sharesPlayback` sibling from the winner's
 * `playbackGroup`, and keeps one of them audible. Without it every in-view video autoplays at once.
 */
export const PageVideoCoordinatorProvider = ({ children }: { children: ReactNode }) => {
    const entries = useRef(new Map<string, VideoEntry>());
    const [sound, setSound] = useState<SoundState>({ enabled: false, ownerId: null });

    const recompute = useCallback(() => {
        const winner = electWinner(entries.current);

        // Only notify videos whose play state actually flips, to avoid churn while scrolling.
        const playingIds = new Set<string>();
        entries.current.forEach((entry, id) => {
            const shouldPlay = id === winner.id || joinsWinner(entry, winner);
            if (entry.playing !== shouldPlay) {
                entry.playing = shouldPlay;
                entry.setPlaying(shouldPlay);
            }
            if (shouldPlay) {
                playingIds.add(id);
            }
        });

        // `playingIds` is captured rather than read from the mutable map, so the updater stays pure.
        setSound((current) => withOwner(current, playingIds, winner.id));
    }, []);

    // Debounced so a mid-swipe frame — where the swiped tile's ratio dips below a stationary
    // neighbour's — can't briefly steal playback. `scroll` fires during the snap too, so it's covered.
    const settleRecompute = useDebounce(recompute, SETTLE_MS);

    // Capture: scroll doesn't bubble up from the nested carousel scrollers.
    useEffect(() => {
        document.addEventListener('scroll', settleRecompute, true);
        return () => document.removeEventListener('scroll', settleRecompute, true);
    }, [settleRecompute]);

    const register = useCallback(
        (id: string, setPlaying: (playing: boolean) => void, state: VideoEntryState) => {
            entries.current.set(id, { ...state, ratio: 0, playing: false, setPlaying });
            return () => {
                entries.current.delete(id);
                settleRecompute();
            };
        },
        [settleRecompute]
    );

    const report = useCallback(
        (id: string, ratio: number, isPlaybackCandidate: boolean, sharesPlayback: boolean) => {
            const entry = entries.current.get(id);
            if (!entry) {
                return;
            }
            entry.ratio = ratio;
            entry.isPlaybackCandidate = isPlaybackCandidate;
            entry.sharesPlayback = sharesPlayback;
            settleRecompute();
        },
        [settleRecompute]
    );

    const toggleSound = useCallback((id: string) => {
        // Read outside the updater to keep it pure.
        if (!shouldClaimSound(entries.current, id)) {
            return false;
        }
        setSound((current) =>
            current.ownerId === id ? { ...current, enabled: !current.enabled } : { enabled: true, ownerId: id }
        );
        return true;
    }, []);

    const value = useMemo<PageVideoCoordinator>(
        () => ({ sound, toggleSound, register, report }),
        [sound, toggleSound, register, report]
    );

    return <CoordinatorContext.Provider value={value}>{children}</CoordinatorContext.Provider>;
};

/**
 * Returns null with no provider mounted, which is how every `Video` outside the influencer hub keeps
 * its standalone in-view autoplay untouched.
 */
export const usePageVideoCoordinator = (
    elementId: string,
    isPlaybackCandidate: boolean,
    sharesPlayback = false,
    playbackGroup?: string
): CoordinatedVideoState | null => {
    const coordinator = useContext(CoordinatorContext);
    const [isPlaying, setIsPlaying] = useState(false);
    const isCandidateRef = useRef(isPlaybackCandidate);
    const sharesPlaybackRef = useRef(sharesPlayback);
    const ratioRef = useRef(0);

    // Depend on the stable `register`/`report`, not the whole coordinator object — it is rebuilt on
    // every `soundEnabled` change, and re-registering on each mute toggle desyncs `playing` (>1 plays).
    const register = coordinator?.register;
    const report = coordinator?.report;

    useEffect(() => {
        if (!register || !report) {
            return undefined;
        }
        const unregister = register(elementId, setIsPlaying, {
            isPlaybackCandidate: isCandidateRef.current,
            sharesPlayback: sharesPlaybackRef.current,
            playbackGroup
        });
        const element = document.getElementById(elementId);
        if (!element) {
            return unregister;
        }
        // `Video`'s own observer only stops it while off screen, so this ratio alone decides who plays.
        const observer = new IntersectionObserver(
            ([entry]) => {
                ratioRef.current = entry.isIntersecting ? entry.intersectionRatio : 0;
                report(elementId, ratioRef.current, isCandidateRef.current, sharesPlaybackRef.current);
            },
            { threshold: RATIO_THRESHOLDS }
        );
        observer.observe(element);
        return () => {
            observer.disconnect();
            unregister();
            // Match the fresh (playing:false) entry a re-registration creates, so the flip-only
            // notification can't leave this video stuck playing (no-op on a real unmount).
            setIsPlaying(false);
        };
    }, [register, report, elementId, playbackGroup]);

    useEffect(() => {
        isCandidateRef.current = isPlaybackCandidate;
        sharesPlaybackRef.current = sharesPlayback;
        report?.(elementId, ratioRef.current, isPlaybackCandidate, sharesPlayback);
    }, [report, elementId, isPlaybackCandidate, sharesPlayback]);

    if (!coordinator) {
        return null;
    }
    return {
        isPlaying,
        soundEnabled: coordinator.sound.enabled && coordinator.sound.ownerId === elementId,
        toggleSound: () => coordinator.toggleSound(elementId)
    };
};
